// =============================================================================
// lib/image.ts — shrink a phone photo before upload
// =============================================================================
// Report photos come straight off a phone camera (3 to 8 MB). Nobody needs
// that to judge "the headset showed a black screen", and the run-reports
// bucket caps objects at 10 MB, so the longest edge is capped and the result
// re-encoded as JPEG. Anything the browser cannot decode is passed through
// untouched and the bucket's own limits decide.

export const REPORT_PHOTO_MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
}

export async function downscaleImage(
  file: Blob,
  maxEdge: number = REPORT_PHOTO_MAX_EDGE,
): Promise<{ blob: Blob; contentType: string }> {
  try {
    const src = await decode(file);
    const w = 'naturalWidth' in src ? src.naturalWidth : src.width;
    const h = 'naturalHeight' in src ? src.naturalHeight : src.height;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
    if ('close' in src) src.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) throw new Error('toBlob returned null');
    return { blob, contentType: 'image/jpeg' };
  } catch {
    return { blob: file, contentType: file.type || 'application/octet-stream' };
  }
}
